import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { noticiaService } from '@/services/noticia.service'
import type { NoticiaPortal, UUID } from '@/types/database'

const KEYS = {
    all: ['noticias_portal'] as const,
    detail: (id: string) => ['noticia_portal', id] as const,
}

export function useNoticias() {
    return useQuery({
        queryKey: KEYS.all,
        queryFn: () => noticiaService.list()
    })
}

export function useNoticia(id?: string) {
    return useQuery({
        queryKey: KEYS.detail(id || 'none'),
        queryFn: () => id ? noticiaService.get(id) : null,
        enabled: !!id
    })
}

export function useCreateNoticia() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: Partial<NoticiaPortal>) => noticiaService.create(data),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all })
    })
}

export function useUpdateNoticia() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ id, ...data }: Partial<NoticiaPortal> & { id: UUID }) =>
            noticiaService.update(id, data),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: KEYS.all })
            queryClient.invalidateQueries({ queryKey: KEYS.detail(id) })
        }
    })
}

export function useDeleteNoticia() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: UUID) => noticiaService.remove(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all })
    })
}
